import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc
} from "firebase/firestore";
import { QRCodeCanvas } from "qrcode.react";
import "../styles/Location.css";
import * as htmlToImage from "html-to-image";
import colors from "react-multi-date-picker/plugins/colors";

function Location() {
  const [location, setLocation] = useState("");
  const [company, setCompany] = useState("");
  const [isSecurityPoint, setIsSecurityPoint] = useState(false);
  const [checklistItem, setChecklistItem] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(""); // error for duplicates
  const [searchTerm, setSearchTerm] = useState(""); // search input
  const [currentPage, setCurrentPage] = useState(1); // current page number
  const itemsPerPage = 10; // number of items per page
  const companyOptions = ["Sarathchandra Textile", "Centauria Lake", "Centauria Wild", "Home Center", "Other"]; // predefined company options
  
  
  const filteredLocations = locations
  .slice()
  .sort((a, b) => a.location.localeCompare(b.location))
  .filter((loc) =>
    loc.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLocations = filteredLocations.slice(indexOfFirstItem, indexOfLastItem);

  const locationsCollection = collection(db, "locations");

  // Fetch all locations
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    const data = await getDocs(locationsCollection);
    setLocations(data.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  const addChecklistItem = () => {
    if (checklistItem.trim()) {
      setChecklist([...checklist, checklistItem]);
      setChecklistItem("");
    }
  };

  const handleSave = async () => {
    if (!location.trim()) return;

    if (isSecurityPoint && !company) {
      setErrorMessage("Please select a company for this security point.");
      return;
    }

    // Check for duplicate (case-insensitive)
    const isDuplicate = locations.some(
      (loc) => loc.location.toLowerCase() === location.toLowerCase()
    );

    if (isDuplicate && !editingId) {
      setErrorMessage("⚠️ Location already registered!");
      return;
    }

    setErrorMessage(""); // clear error
    const qrValue = location;
    const newData = { location, company, checklist, qrValue, isSecurityPoint };

    if (editingId) {
      const locationDoc = doc(db, "locations", editingId);
      await updateDoc(locationDoc, newData);
      setEditingId(null);
      alert("Location updated successfully!");
    } else {
      await addDoc(locationsCollection, newData);
      alert("Location added successfully!");
    }

    setLocation("");
    setCompany("");
    setIsSecurityPoint(false);
    setChecklist([]);
    fetchLocations();
  };

  const handleEdit = (loc) => {
    setLocation(loc.location);
    setCompany(loc.company || "");
    setIsSecurityPoint(loc.isSecurityPoint === true);
    setChecklist(loc.checklist || []);
    setEditingId(loc.id);
    setErrorMessage(""); // reset error
    alert(`Editing location: ${loc.location}`);
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this location?"
    );
    if (confirmDelete) {
      await deleteDoc(doc(db, "locations", id));
      fetchLocations();
      alert("Location deleted successfully!");
    }
  };

  const handleChecklistChange = (index, value) => {
    const updatedChecklist = [...checklist];
    updatedChecklist[index] = value;
    setChecklist(updatedChecklist);
  };

  const removeChecklistItem = (index) => {
    const updatedChecklist = checklist.filter((_, i) => i !== index);
    setChecklist(updatedChecklist);
  };

  // ✅ Download QR with text under it
  const downloadQR = (id, label) => {
    const node = document.getElementById(id);
    htmlToImage.toPng(node).then((dataUrl) => {
      const link = document.createElement("a");
      link.download = `${label}_QR.png`;
      link.href = dataUrl;
      link.click();
    });
  };

  // ✅ Filter locations by searchTerm
  // const filteredLocations = locations
  //   .slice()
  //   .sort((a, b) => a.location.localeCompare(b.location))
  //   .filter((loc) =>
  //     loc.location.toLowerCase().includes(searchTerm.toLowerCase())
  //   );

  return (
    <div className="location-container">
      <h2>Register New Location</h2>

      {/* Form */}
      <div className="form-section">
        <input
          type="text"
          placeholder="Enter Location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />

        <select
          className="location-company-select"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          aria-label="Select company for this QR point"
        >
          <option value="">Select Company</option>
          {companyOptions.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>

        <label className="security-point-option">
          <input
            type="checkbox"
            checked={isSecurityPoint}
            onChange={(e) => setIsSecurityPoint(e.target.checked)}
          />
          Mark this QR code as a security point
        </label>

        {/* Show error message if duplicate */}
        {errorMessage && <p className="error-message">{errorMessage}</p>}

        {/* Checklist input */}
        <div className="checklist-input">
          <input
            type="text"
            placeholder="Add checklist item"
            value={checklistItem}
            onChange={(e) => setChecklistItem(e.target.value)}
          />
          <button onClick={addChecklistItem}>Add Item</button>
        </div>

        {/* Editable checklist */}
        <ul className="checklist-display">
          {checklist.map((item, idx) => (
            <li key={idx}>
              <input
                type="text"
                value={item}
                onChange={(e) => handleChecklistChange(idx, e.target.value)}
              />
              <button onClick={() => removeChecklistItem(idx)}>❌</button>
            </li>
          ))}
        </ul>

        {/* QR preview */}
        {location && (
          <div className="qr-preview">
            <QRCodeCanvas value={location} size={128} />
          </div>
        )}

        {/* Save button */}
        <button onClick={handleSave}>
          {editingId ? "Update Location" : "Add Location"}
        </button>
      </div>

     
      {/* Table */}
      <h3>Saved Locations</h3>

       {/* Search bar */}
      <div className="search-section">
        <input
          type="text"
          placeholder="🔍 Search locations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <p className="p1"> when create the new location please enter the hotel name first </p>
      <br></br>
      <table className="location-table">
        <thead>
          <tr>
            <th>No</th>
            <th>Company</th>
            <th>Location</th>
            <th>QR Type</th>
            <th>Checklist</th>
            <th>QR Code</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentLocations.map((loc , index) => (
            <tr key={loc.id}>              
              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
              <td>{loc.company || "Company not set"}</td>
              <td>{loc.location}</td>
              <td>{loc.isSecurityPoint === true ? "Security" : "Not marked as security"}</td>
              <td>
                <ul>
                  {loc.checklist?.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </td>
              <td>
                <div id={`qr-${loc.id}`} style={{ textAlign: "center" }}>
                  <QRCodeCanvas value={loc.qrValue} size={210} />
                  <div>{loc.location}</div>
                </div>
              </td>
              <td>
                <button onClick={() => handleEdit(loc)}>Edit</button>
                <button onClick={() => handleDelete(loc.id)}>Delete</button>
                <button
                  onClick={() => downloadQR(`qr-${loc.id}`, loc.location)}
                >
                  Download QR
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Previous
        </button>
        <span style={{ margin: "0 10px" }}>Page {currentPage}</span>
        <button
          disabled={indexOfLastItem >= filteredLocations.length}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default Location;
